import { memo, useEffect, useMemo, useRef } from 'react';
import { contentBBox, hexA, type BoardDoc, type Rect } from '@scribbleplot/shared';
import { color } from '../tokens';
import { useCamera } from './camera';
import { startMinimapPan } from './gestures';

/**
 * Overview map, bottom-right.
 *
 * Shows the union of the board's content and the current viewport, so the view
 * rectangle stays visible even when you pan far off into empty space. Clicking
 * or dragging recentres the camera.
 */

const MAP_W = 168;
const MAP_H = 108;
const INNER_W = 160;
const INNER_H = 100;
const PAD = 80;

type Projection = { scale: number; originX: number; originY: number; offsetX: number; offsetY: number };

export function Minimap({ doc, accent }: { doc: BoardDoc; accent: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const cam = useCamera((s) => s.cam);
  const cameraApi = useCamera;
  const projectionRef = useRef<Projection | null>(null);

  const view = cameraApi.getState().viewRect();
  const content = useMemo(() => contentBBox(doc), [doc]);

  const projection = useMemo<Projection | null>(() => {
    if (!view) return null;
    let x0 = view.x,
      y0 = view.y,
      x1 = view.x + view.w,
      y1 = view.y + view.h;
    if (content) {
      x0 = Math.min(x0, content.x);
      y0 = Math.min(y0, content.y);
      x1 = Math.max(x1, content.x + content.w);
      y1 = Math.max(y1, content.y + content.h);
    }
    x0 -= PAD;
    y0 -= PAD;
    x1 += PAD;
    y1 += PAD;

    const width = Math.max(1, x1 - x0);
    const height = Math.max(1, y1 - y0);
    const scale = Math.min(INNER_W / width, INNER_H / height);
    return {
      scale,
      originX: x0,
      originY: y0,
      // Centre the projected extent inside the map.
      offsetX: (MAP_W - width * scale) / 2,
      offsetY: (MAP_H - height * scale) / 2,
    };
  }, [content, view?.x, view?.y, view?.w, view?.h]);

  projectionRef.current = projection;

  /**
   * The gesture machine drives panning through a window event, because the drag
   * continues after the pointer leaves this small element.
   */
  useEffect(() => {
    const onPan = (event: Event) => {
      const detail = (event as CustomEvent<{ clientX: number; clientY: number }>).detail;
      const element = ref.current;
      const proj = projectionRef.current;
      if (!element || !proj) return;
      const rect = element.getBoundingClientRect();
      const wx = proj.originX + (detail.clientX - rect.left - proj.offsetX) / proj.scale;
      const wy = proj.originY + (detail.clientY - rect.top - proj.offsetY) / proj.scale;
      const camera = useCamera.getState();
      const viewport = camera.viewport;
      if (!viewport) return;
      const vpRect = viewport.getBoundingClientRect();
      camera.set({
        ...camera.cam,
        x: vpRect.width / 2 - wx * camera.cam.s,
        y: vpRect.height / 2 - wy * camera.cam.s,
      });
    };
    window.addEventListener('sp:minimap-pan', onPan);
    return () => window.removeEventListener('sp:minimap-pan', onPan);
  }, []);

  if (!projection || !view) return null;

  const project = (rect: Rect) => ({
    left: projection.offsetX + (rect.x - projection.originX) * projection.scale,
    top: projection.offsetY + (rect.y - projection.originY) * projection.scale,
    width: Math.max(2, rect.w * projection.scale),
    height: Math.max(2, rect.h * projection.scale),
  });

  return (
    <div
      className="sp-minimap"
      ref={ref}
      role="button"
      aria-label="Board overview — click to navigate"
      onPointerDown={(event) => {
        event.stopPropagation();
        startMinimapPan(event);
      }}
      style={{
        position: 'absolute',
        right: 12,
        bottom: 56,
        width: MAP_W,
        height: MAP_H,
        background: 'rgba(30,31,36,.92)',
        border: `1px solid ${color.lineSoft}`,
        borderRadius: 12,
        zIndex: 50,
        overflow: 'hidden',
        cursor: 'crosshair',
      }}
    >
      <svg
        aria-hidden
        width={MAP_W}
        height={MAP_H}
        viewBox={`${projection.originX - projection.offsetX / projection.scale} ${projection.originY - projection.offsetY / projection.scale} ${MAP_W / projection.scale} ${MAP_H / projection.scale}`}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <MinimapItems doc={doc} accent={accent} />
      </svg>

      <div
        aria-hidden
        style={{
          position: 'absolute',
          ...project(view),
          border: `1.5px solid ${accent}`,
          background: hexA(accent, 0.08),
          borderRadius: 2,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// Content marks stay in world coordinates. Panning updates the SVG viewBox and
// viewport rectangle without recreating thousands of React elements.
const MinimapItems = memo(function MinimapItems({ doc, accent }: { doc: BoardDoc; accent: string }) {
  return (
    <>
      {doc.items.map((item) => (
        <rect
          key={item.id}
          x={item.x}
          y={item.y}
          width={Math.max(1, item.w)}
          height={Math.max(1, item.h)}
          fill={
            item.type === 'sticky'
              ? item.color
              : item.type === 'group'
                ? accent
                : item.type === 'frame'
                  ? 'transparent'
                  : '#4A4E5C'
          }
          stroke={item.type === 'frame' ? 'rgba(255,255,255,.25)' : 'none'}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          opacity={0.85}
        />
      ))}
    </>
  );
});
